import {
    ChangeDetectorRef,
    Component,
    OnInit,
    forwardRef,
    ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { AuthService } from 'app/core/auth/auth.service';
import { TeamService } from 'app/shared/apis/user/api/team.service';
import { GroupService } from 'app/shared/apis/user/api/group.service';
import { TeamDto } from 'app/shared/apis/user/model/teamDto';
import { GroupDto } from 'app/shared/apis/user/model/groupDto';
import {
    AudienceDto,
    AudienceMode,
} from 'app/shared/apis/product/model/audienceDto';
import { SegOption } from '../segmented-control/segmented-control.component';

// SESSION-admin-ptg-002 — shared `[data-audience]` control. Renders the
// segmented Everyone⇄Specific toggle + the teams/groups checklist, reading real
// Teams (GET /api/v1/Team) and Groups (GET /api/v1/Group). Persists
// { mode, teamIds, groupIds }. CVA so it binds via ngModel / formControlName.
// Reused by the Add-product, Add-presentation, Upload-docs and Share modals.
@Component({
    selector: 'app-audience-picker',
    templateUrl: './audience-picker.component.html',
    encapsulation: ViewEncapsulation.None,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => AudiencePickerComponent),
            multi: true,
        },
    ],
})
export class AudiencePickerComponent implements ControlValueAccessor, OnInit {
    AudienceMode = AudienceMode;

    modeOptions: SegOption[] = [
        { value: AudienceMode.Everyone, label: 'Everyone · all Inside Sales' },
        { value: AudienceMode.Specific, label: 'Specific teams & groups' },
    ];

    teams: TeamDto[] = [];
    groups: GroupDto[] = [];
    loading = false;

    value: AudienceDto = {
        mode: AudienceMode.Everyone,
        teamIds: [],
        groupIds: [],
    };

    private onChange: (v: AudienceDto) => void = () => {};
    private onTouched: () => void = () => {};

    constructor(
        private _authService: AuthService,
        private _teamService: TeamService,
        private _groupService: GroupService,
        private _cdr: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this.loadAudienceSources();
    }

    // ── ControlValueAccessor ──
    writeValue(v: AudienceDto): void {
        this.value = this.normalize(v);
    }
    registerOnChange(fn: (v: AudienceDto) => void): void {
        this.onChange = fn;
    }
    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    private normalize(v: AudienceDto): AudienceDto {
        return {
            mode: v?.mode ?? AudienceMode.Everyone,
            teamIds: v?.teamIds ? [...v.teamIds] : [],
            groupIds: v?.groupIds ? [...v.groupIds] : [],
        };
    }

    get isSpecific(): boolean {
        return this.value.mode === AudienceMode.Specific;
    }

    setMode(mode: AudienceMode): void {
        this.value = { ...this.value, mode };
        this.emit();
    }

    toggleTeam(id: string): void {
        this.value.teamIds = this.toggle(this.value.teamIds, id);
        this.emit();
    }

    toggleGroup(id: string): void {
        this.value.groupIds = this.toggle(this.value.groupIds, id);
        this.emit();
    }

    isTeamChecked(id: string): boolean {
        return (this.value.teamIds || []).includes(id);
    }

    isGroupChecked(id: string): boolean {
        return (this.value.groupIds || []).includes(id);
    }

    private toggle(list: string[] | undefined, id: string): string[] {
        const next = [...(list || [])];
        const i = next.indexOf(id);
        if (i >= 0) {
            next.splice(i, 1);
        } else {
            next.push(id);
        }
        return next;
    }

    private emit(): void {
        this.onChange(this.value);
        this.onTouched();
    }

    private loadAudienceSources(): void {
        const tenantId = this._authService.selectedTenant?.id;
        if (!tenantId) {
            return;
        }
        this.loading = true;
        this._teamService.teamGet(tenantId).subscribe(
            (res) => {
                this.teams = res?.result || [];
                this._cdr.markForCheck();
            },
            () => {}
        );
        this._groupService.groupGet(tenantId).subscribe(
            (res) => {
                this.groups = res?.result || [];
                this.loading = false;
                this._cdr.markForCheck();
            },
            () => {
                this.loading = false;
            }
        );
    }
}
